import { useId, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, PlugZap, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  DEFAULT_STYLE_GUIDE,
  MODULE_CATALOG,
  type StyleGuide,
  type TenantSummary,
} from '@vce/shared';
import { PageHeader } from '@/app/layout';
import { useAuth } from '@/app/auth-context';
import { useTenantReady } from '@/app/tenant-context';
import { NoTenantState } from '@/components/no-tenant-state';
import { TenantFormDialog } from '@/components/tenant-form-dialog';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Input,
  QueryState,
  Spinner,
  Textarea,
} from '@/components/ui/primitives';
import { api, ApiError } from '@/lib/api';
import { formatNumber, formatRelative } from '@/lib/utils';

export function SettingsPage() {
  const { can, user } = useAuth();
  const { tenant, tenantId, isResolving } = useTenantReady();
  const queryClient = useQueryClient();
  const canEdit = can('admin');
  const isOwner = user?.role === 'owner';
  const isTester = user?.isTester === true;
  const canTest = canEdit || isTester;
  const [creatingTenant, setCreatingTenant] = useState(false);

  const tenantsQuery = useQuery({
    queryKey: ['tenants'],
    queryFn: api.tenants.list,
    enabled: isTester,
  });
  const testerTenants = tenantsQuery.data ?? [];
  const hasTesterStores = isTester && testerTenants.length > 0;
  // Sem lojas: CTA no header. Com lojas: também no header para cadastrar outra.
  const showCadastreInHeader = isTester && !tenantsQuery.isLoading;

  const description = tenant
    ? `${tenant.name} · ${tenant.accountName}.${tenant.environment}.com.br`
    : MODULE_CATALOG.settings.description;

  return (
    <>
      <PageHeader
        title={MODULE_CATALOG.settings.label}
        description={description}
        actions={
          showCadastreInHeader ? (
            <Button size="sm" variant="primary" onClick={() => setCreatingTenant(true)}>
              <Plus className="size-3.5" aria-hidden />
              Cadastrar conta
            </Button>
          ) : undefined
        }
      />

      <div className="p-4 sm:p-6">
        {isResolving ? (
          <Spinner label="Carregando conta…" />
        ) : (
          <div className="mx-auto space-y-5">
            {tenant && tenantId ? (
              <>
                <AccountCard
                  tenant={tenant}
                  canTest={canTest}
                  isOwner={isOwner}
                  isTester={isTester}
                />
                <StyleGuideCard tenantId={tenantId} canEdit={canEdit} />
              </>
            ) : null}

            {!tenant && !isTester ? <NoTenantState /> : null}

            {hasTesterStores ? (
              <TesterAccountsCard
                tenants={testerTenants}
                isLoading={tenantsQuery.isLoading}
                isError={tenantsQuery.isError}
                onRetry={() => void tenantsQuery.refetch()}
              />
            ) : null}
          </div>
        )}
      </div>

      {creatingTenant ? (
        <TenantFormDialog
          onClose={() => setCreatingTenant(false)}
          onSaved={() => {
            setCreatingTenant(false);
            void queryClient.invalidateQueries({ queryKey: ['tenants'] });
          }}
        />
      ) : null}
    </>
  );
}

function TesterAccountsCard({
  tenants,
  isLoading,
  isError,
  onRetry,
}: {
  tenants: TenantSummary[];
  isLoading: boolean;
  isError: boolean;
  onRetry(): void;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<TenantSummary | null>(null);

  return (
    <>
      <Card className="min-w-0">
        <CardHeader
          title="Minhas contas de teste"
          description="Ingestão e geração limitadas a 5 produtos por conta."
        />
        <QueryState isLoading={isLoading} isError={isError} onRetry={onRetry}>
          <ul className="divide-y divide-[var(--color-hairline)]">
            {tenants.map((item) => (
              <li
                key={item.id}
                className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[var(--color-ink)]">{item.name}</p>
                  <p className="text-xs text-[var(--color-muted)]">
                    {item.accountName}.{item.environment}.com.br
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {item.isSandbox ? <Badge tone="brand">sandbox</Badge> : null}
                    {!item.isActive ? <Badge tone="neutral">inativa</Badge> : null}
                    {!item.hasCredentials ? <Badge tone="critical">sem credencial</Badge> : null}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => setEditing(item)}
                  aria-label={`Editar ${item.name}`}
                >
                  <Pencil className="size-3.5" aria-hidden />
                  Editar
                </Button>
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>

      {editing ? (
        <TenantFormDialog
          tenant={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['tenants'] });
          }}
        />
      ) : null}
    </>
  );
}

function AccountCard({
  tenant,
  canTest,
  isOwner,
  isTester,
}: {
  tenant: TenantSummary;
  canTest: boolean;
  isOwner: boolean;
  isTester: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const test = useMutation({
    mutationFn: () => api.tenants.testConnection(tenant.id),
    onSuccess: (result) => {
      if (result.ok) {
        toast.success(
          `${result.message} · ${result.sampleProductIds.length} produtos em ${result.latencyMs}ms`,
        );
      } else {
        toast.error(result.message);
      }
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'Falha no teste de conexão'),
  });

  return (
    <>
      <Card>
        <CardHeader
          title="Conta VTEX"
          description="Status e conexão da loja selecionada."
          action={
            <div className="flex shrink-0 flex-wrap justify-end gap-2">
              {canTest ? (
                <Button
                  size="sm"
                  disabled={test.isPending || !tenant.hasCredentials}
                  onClick={() => test.mutate()}
                  aria-label={`Testar conexão de ${tenant.name}`}
                >
                  <PlugZap className="size-3.5" aria-hidden />
                  {test.isPending ? 'Testando…' : 'Testar conexão'}
                </Button>
              ) : null}
              {isTester ? (
                <Button
                  size="sm"
                  onClick={() => setEditing(true)}
                  aria-label={`Editar ${tenant.name}`}
                >
                  <Pencil className="size-3.5" aria-hidden />
                  Editar
                </Button>
              ) : null}
              {isOwner ? (
                <Button size="sm" onClick={() => navigate('/admin?tab=accounts')}>
                  Gerenciar contas
                </Button>
              ) : null}
            </div>
          }
        />
        <div className="space-y-4 px-5 py-4">
          <div>
            <p className="text-sm font-medium text-[var(--color-ink)]">{tenant.name}</p>
            <p className="text-xs text-[var(--color-muted)]">
              {tenant.accountName}.{tenant.environment}.com.br
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              {tenant.isSandbox ? <Badge tone="brand">sandbox</Badge> : null}
              {!tenant.isActive ? <Badge tone="neutral">inativa</Badge> : null}
              {tenant.hasCredentials ? (
                <Badge tone="success">credencial ok</Badge>
              ) : (
                <Badge tone="critical">sem credencial</Badge>
              )}
            </div>
          </div>
          <dl className="grid gap-3 sm:grid-cols-3">
            <Metric label="Produtos" value={formatNumber(tenant.productCount)} />
            <Metric label="Última ingestão" value={formatRelative(tenant.lastIngestionAt)} />
            <Metric
              label="Score médio"
              value={tenant.averageScore == null ? '—' : tenant.averageScore.toFixed(0)}
            />
          </dl>
        </div>
      </Card>
      {editing ? (
        <TenantFormDialog
          tenant={tenant}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void queryClient.invalidateQueries({ queryKey: ['tenants'] });
          }}
        />
      ) : null}
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[10px] border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] px-3 py-2.5">
      <dt className="text-[11px] font-medium uppercase tracking-[1.5px] text-[var(--color-muted)]">
        {label}
      </dt>
      <dd className="mt-1 text-sm font-semibold tabular-nums text-[var(--color-ink)]">{value}</dd>
    </div>
  );
}

function StyleGuideCard({ tenantId, canEdit }: { tenantId: string; canEdit: boolean }) {
  const query = useQuery({
    queryKey: ['style-guide', tenantId],
    queryFn: () => api.tenants.getStyleGuide(tenantId),
  });

  return (
    <Card>
      <CardHeader
        title="Como a IA escreve"
        description="Tom, vocabulário e regras desta loja. Não altera políticas de SEO nem do Merchant Center."
        action={!canEdit ? <Badge tone="neutral">somente leitura</Badge> : null}
      />
      {query.isLoading ? (
        <Spinner />
      ) : query.isError ? (
        <EmptyState
          title="Não foi possível carregar o guia"
          action={
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Tentar de novo
            </Button>
          }
        />
      ) : (
        <StyleGuideForm
          key={tenantId}
          tenantId={tenantId}
          initial={query.data ?? DEFAULT_STYLE_GUIDE}
          canEdit={canEdit}
        />
      )}
    </Card>
  );
}

function StyleGuideForm({
  tenantId,
  initial,
  canEdit,
}: {
  tenantId: string;
  initial: StyleGuide;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const languageId = useId();
  const toneId = useId();
  const requiredId = useId();
  const forbiddenId = useId();
  const extraId = useId();
  const [form, setForm] = useState<StyleGuide>(initial);

  const save = useMutation({
    mutationFn: () => api.tenants.saveStyleGuide(tenantId, form),
    onSuccess: () => {
      toast.success('Guia de estilo salvo');
      void queryClient.invalidateQueries({ queryKey: ['style-guide', tenantId] });
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'Falha ao salvar'),
  });

  return (
    <div className="space-y-5 px-5 py-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <LabeledField label="Idioma" htmlFor={languageId} hint="Ex.: pt-BR">
          <Input
            id={languageId}
            placeholder="pt-BR"
            value={form.language}
            disabled={!canEdit}
            onChange={(event) => setForm({ ...form, language: event.target.value })}
          />
        </LabeledField>
        <LabeledField
          label="Tom de voz"
          htmlFor={toneId}
          hint="Como a marca fala nos textos"
          className="sm:col-span-2"
        >
          <Textarea
            id={toneId}
            rows={3}
            disabled={!canEdit}
            placeholder="Claro, objetivo e informativo. Sem superlativos vazios."
            value={form.toneOfVoice}
            onChange={(event) => setForm({ ...form, toneOfVoice: event.target.value })}
          />
        </LabeledField>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <LabeledField
          label="Termos preferidos"
          htmlFor={requiredId}
          hint="Separados por vírgula"
        >
          <Input
            id={requiredId}
            placeholder="ex.: original, com nota fiscal"
            value={form.requiredTerms.join(', ')}
            disabled={!canEdit}
            onChange={(event) =>
              setForm({ ...form, requiredTerms: splitTerms(event.target.value) })
            }
          />
        </LabeledField>
        <LabeledField
          label="Termos proibidos"
          htmlFor={forbiddenId}
          hint="Bloqueados na geração"
        >
          <Input
            id={forbiddenId}
            placeholder="ex.: imperdível, melhor do Brasil"
            value={form.forbiddenTerms.join(', ')}
            disabled={!canEdit}
            onChange={(event) =>
              setForm({ ...form, forbiddenTerms: splitTerms(event.target.value) })
            }
          />
        </LabeledField>
      </div>

      <LabeledField
        label="Instruções extras"
        htmlFor={extraId}
        hint="Regras desta loja (preço, frete e promoção continuam proibidos)"
      >
        <Textarea
          id={extraId}
          rows={3}
          disabled={!canEdit}
          placeholder="ex.: Sempre mencionar a linha do produto quando houver na especificação."
          value={form.extraInstructions}
          onChange={(event) => setForm({ ...form, extraInstructions: event.target.value })}
        />
      </LabeledField>

      {canEdit ? (
        <div className="flex justify-end border-t border-[var(--color-hairline)] pt-4">
          <Button
            variant="primary"
            size="sm"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Salvando…' : 'Salvar guia'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function splitTerms(value: string): string[] {
  return value
    .split(',')
    .map((term) => term.trim())
    .filter((term) => term.length > 0);
}

function LabeledField({
  label,
  htmlFor,
  hint,
  children,
  className,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="mb-1 block text-xs font-medium text-[var(--color-body)]">
        {label}
      </label>
      {hint ? (
        <p className="mb-1.5 text-[11px] leading-snug text-[var(--color-muted)]">{hint}</p>
      ) : null}
      {children}
    </div>
  );
}
